/**
 * CellX first-party attribution — Creative OS M3c
 * Captures UTMs / fbclid / landing into cookie + localStorage,
 * then writes cart attributes that Rebuy does NOT own.
 *
 * Design:
 * - Rebuy Smart Cart overwrites `_attribution` = "Smart Cart 2.0"
 * - We never fight that key. We write parallel first-party keys:
 *     utm_source, utm_medium, utm_campaign, utm_content, utm_term,
 *     cx_fbclid, cx_gclid, cx_landing, cx_attr_json, cx_registry_id
 * - Cookies last 30 days; cart attrs refreshed on every page load + add-to-cart.
 */
(function () {
  'use strict';
  if (window.__cellxAttrInit) return;
  window.__cellxAttrInit = true;

  var COOKIE = 'cx_attr_v1';
  var DAYS = 30;
  var KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'gclid',
    'ttclid'
  ];

  function qs() {
    try {
      return new URLSearchParams(window.location.search || '');
    } catch (e) {
      return new URLSearchParams();
    }
  }

  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch (e) {
      return m[1];
    }
  }

  function writeCookie(name, value, days) {
    var maxAge = Math.max(1, Math.floor(days * 86400));
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      '; path=/; max-age=' +
      maxAge +
      '; SameSite=Lax';
  }

  function loadStored() {
    var raw = readCookie(COOKIE);
    if (!raw) {
      try {
        raw = localStorage.getItem(COOKIE);
      } catch (e) {}
    }
    if (!raw) return {};
    try {
      var o = JSON.parse(raw);
      return o && typeof o === 'object' ? o : {};
    } catch (e) {
      return {};
    }
  }

  function saveStored(obj) {
    var s = JSON.stringify(obj);
    writeCookie(COOKIE, s, DAYS);
    try {
      localStorage.setItem(COOKIE, s);
    } catch (e) {}
  }

  function capture() {
    var params = qs();
    var stored = loadStored();
    var changed = false;
    var now = new Date().toISOString();

    KEYS.forEach(function (k) {
      var v = params.get(k);
      if (v && String(v).trim()) {
        // First-touch win: keep original unless empty
        if (!stored[k]) {
          stored[k] = String(v).trim().slice(0, 500);
          changed = true;
        }
        // Last-touch mirror
        stored['last_' + k] = String(v).trim().slice(0, 500);
        changed = true;
      }
    });

    if (!stored.landing) {
      stored.landing = (window.location.pathname + window.location.search).slice(0, 900);
      changed = true;
    }
    stored.last_landing = (window.location.pathname + window.location.search).slice(0, 900);
    stored.updated_at = now;
    if (!stored.first_seen_at) stored.first_seen_at = now;

    // Prefer explicit utm_content as registry id
    if (stored.utm_content) stored.registry_id = stored.utm_content;
    if (stored.last_utm_content) stored.last_registry_id = stored.last_utm_content;

    if (changed || !readCookie(COOKIE)) saveStored(stored);
    return stored;
  }

  function cartAttributes(stored) {
    var attrs = {
      utm_source: stored.utm_source || stored.last_utm_source || '',
      utm_medium: stored.utm_medium || stored.last_utm_medium || '',
      utm_campaign: stored.utm_campaign || stored.last_utm_campaign || '',
      utm_content: stored.utm_content || stored.last_utm_content || '',
      utm_term: stored.utm_term || stored.last_utm_term || '',
      cx_fbclid: stored.fbclid || stored.last_fbclid || '',
      cx_gclid: stored.gclid || stored.last_gclid || '',
      cx_landing: stored.landing || '',
      cx_registry_id: stored.registry_id || stored.utm_content || '',
      cx_attr_json: JSON.stringify({
        utm_source: stored.utm_source || null,
        utm_medium: stored.utm_medium || null,
        utm_campaign: stored.utm_campaign || null,
        utm_content: stored.utm_content || null,
        utm_term: stored.utm_term || null,
        fbclid: stored.fbclid || null,
        landing: stored.landing || null,
        first_seen_at: stored.first_seen_at || null,
        source: 'cellx-creative-os'
      }).slice(0, 1900)
    };
    // Drop empties
    Object.keys(attrs).forEach(function (k) {
      if (!attrs[k]) delete attrs[k];
    });
    return attrs;
  }

  /* 2026-10-18 (Herm, Hagen-approved via Grok Bot; preview theme first): rate-safe cart writes.
     Old behaviour re-POSTed /cart/update.js 800 ms after EVERY button/link click, on every cart:updated
     and on rebuy:cart.change; the dedupe was only stored after a 200, so a 429 caused a retry storm that
     queued ahead of shopper add-to-carts in cx-cart.js and made those adds fail with 429 too.
     Now:
     - Dedupe on the payload only (the first-time cart token no longer forces a 2nd send).
     - No generic click listener, no Rebuy listener.
     - cart:updated -> GET /cart.js and POST only if our attributes differ from what the cart holds.
     - 30 s backoff (sessionStorage) after a 429 or any non-OK/network error.
     - Sent with XMLHttpRequest so it bypasses cx-cart.js' serialized fetch queue (never in front of an
       add-to-cart), and deferred while a shopper cart mutation is in flight or just happened.
     - Max 1 POST per 3 s. Attribute keys/format unchanged. */
  var SENT_KEY = 'cx_attr_sent_v2';
  var BACKOFF_KEY = 'cx_attr_backoff_until';
  var MIN_GAP_MS = 3000;
  var BACKOFF_MS = 30000;
  var lastSendAt = 0;
  var inFlight = false;
  var pendingTimer = null;
  var lastShopperMutationAt = 0;

  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }
  function backoffLeft() { var u = Number(ssGet(BACKOFF_KEY) || 0); return u > Date.now() ? u - Date.now() : 0; }
  function sameAttrs(attrs, cartAttrs) {
    cartAttrs = cartAttrs || {};
    return Object.keys(attrs).every(function (k) { return String(cartAttrs[k] == null ? '' : cartAttrs[k]) === String(attrs[k]); });
  }

  // Track shopper cart mutations (cx-cart.js queue / theme / apps) so we never send in front of them.
  (function watchShopperMutations() {
    var re = /\/cart\/(add|change|clear)(\.js)?(\?|$)/;
    var f = window.fetch;
    if (!f || f.__cxAttrWatch) return;
    var w = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var m = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (m === 'POST' && re.test(String(url).split('#')[0])) lastShopperMutationAt = Date.now();
      } catch (e) {}
      return f.apply(this, arguments);
    };
    w.__cxAttrWatch = true;
    // keep cx-cart's queue marker so it does not re-wrap
    if (f.__cxQueued) w.__cxQueued = true;
    window.fetch = w;
  })();

  function xhr(method, url, body) {
    return new Promise(function (resolve) {
      try {
        var x = new XMLHttpRequest();
        x.open(method, url, true);
        x.setRequestHeader('Accept', 'application/json');
        if (body) x.setRequestHeader('Content-Type', 'application/json');
        x.timeout = 10000;
        x.onload = function () { resolve({ status: x.status, text: x.responseText }); };
        x.onerror = x.ontimeout = x.onabort = function () { resolve({ status: 0, text: '' }); };
        x.send(body || null);
      } catch (e) { resolve({ status: 0, text: '' }); }
    });
  }

  function doPost(payload) {
    inFlight = true;
    lastSendAt = Date.now();
    return xhr('POST', '/cart/update.js', payload).then(function (r) {
      inFlight = false;
      if (r.status >= 200 && r.status < 300) {
        ssSet(SENT_KEY, payload);
        try { return JSON.parse(r.text); } catch (e) { return null; }
      }
      ssSet(BACKOFF_KEY, String(Date.now() + BACKOFF_MS));
      return null;
    });
  }

  // opts.verify: compare against /cart.js first (used after cart:updated).
  function pushCart(attrs, opts) {
    opts = opts || {};
    if (!attrs || !Object.keys(attrs).length) return Promise.resolve(null);
    var payload = JSON.stringify({ attributes: attrs });
    if (!opts.verify && ssGet(SENT_KEY) === payload) return Promise.resolve(null);
    var wait = Math.max(backoffLeft(), lastSendAt + MIN_GAP_MS - Date.now(), lastShopperMutationAt + 2500 - Date.now());
    if (inFlight || wait > 0) {
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(function () { pushCart(attrs, opts); }, Math.max(wait, 500));
      return Promise.resolve(null);
    }
    if (!opts.verify) return doPost(payload);
    return xhr('GET', '/cart.js').then(function (r) {
      if (r.status !== 200) { if (r.status) ssSet(BACKOFF_KEY, String(Date.now() + BACKOFF_MS)); return null; }
      var cart = null;
      try { cart = JSON.parse(r.text); } catch (e) {}
      if (cart && sameAttrs(attrs, cart.attributes)) { ssSet(SENT_KEY, payload); return null; }
      return doPost(payload);
    });
  }

  var stored = capture();
  window.CellXAttribution = {
    get: function () {
      return loadStored();
    },
    refresh: function () {
      stored = capture();
      return pushCart(cartAttributes(stored));
    },
    cartAttributes: function () {
      return cartAttributes(loadStored());
    }
  };

  // Initial cart write (once per session unless the attributes changed)
  setTimeout(function () {
    pushCart(cartAttributes(stored));
  }, 1200);

  // After a cart change (theme/cx-cart dispatch cart:updated): only re-send if the cart lost our attributes.
  var cuTimer = null;
  document.addEventListener('cart:updated', function () {
    clearTimeout(cuTimer);
    cuTimer = setTimeout(function () { pushCart(cartAttributes(loadStored()), { verify: true }); }, 3000);
  });
})();
